import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { StoreBlockedPage } from "@ecommerce/ui-core";
import { useSiteSubscriptionGate } from "@ecommerce/shared-utils";
import { useTheme } from "./util/themes";
import { ThemeWrapper } from "./components/providers/ThemeWrapper";
import { ShippingProvider } from "@ecommerce/checkout";

import DefaultLayout from "./layouts/DefaultLayout";
import CheckoutLayout from "./layouts/CheckoutLayout";

import Home from "./pages/Home";
import ProductosPage from "./pages/ProductosPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import ProductListing from "./components/ProductListing";

import LogIn from "./pages/auth/LogIn";
import Register from "./pages/auth/Register";
import RecuperoContrasenia from "./pages/auth/RecuperoContrasenia";
import ProfilePage from "./pages/ProfilePage";

import CheckoutPage from "./pages/CheckoutPage";
import CheckoutResumeRoute from "./pages/CheckoutResumeRoute";
import CheckoutResumePage from "./pages/CheckoutResumePage";
import CheckoutResumeGuidRoute from "./pages/CheckoutResumeGuidRoute";
import { CheckoutFormContext } from "./components/Checkout/hooks/useCheckoutForm";
import { CheckoutCartReconciliationGate } from "./components/Checkout/CheckoutCartReconciliationGate";

import ActualizacionDatosPage from "./custom/actualizacion-datos/ActualizacionDatosPage";

import FAQsPage from "./pages/FAQsPage";
import ScrollToTop from "./components/ScrollToTop";

import { ErrorBoundary } from "react-error-boundary";
import { AppErrorFallback } from "./components/errors/AppErrorFallback";
import { HttpLogger, HttpSiteSubscriptionStatus, HttpTiendaInfo } from "./http";

import { SecurityObjectProvider } from "./security/SecurityObjectProvider";
import { SafetyRouteMiddleware } from "./components/security/SafetyRouteMiddleware";
import { TiendaSecurityObjects } from "./types/security";
import { ensureMercadoPagoInitialized } from "@ecommerce/payment-gateways";
import PoliticaPrivacidadPage from "./pages/PoliticaPrivacidadPage";
import MercadoPagoReturnPage from "./pages/MercadoPagoReturnPage";
import MercadoPagoFailureReturnPage from "./pages/MercadoPagoFailureReturnPage";

import { ProductCategoryProvider } from "@/contexts/ProductCategoryContext";
import { PricingSessionProvider } from "@/contexts/PricingSessionContext";
import { UserDataProvider } from "@/contexts/UserDataContext";
import { TiendaInfoProvider } from "@/contexts/TiendaInfoContext";

ensureMercadoPagoInitialized();

function App() {
    const theme = useTheme();
    const { isStoreGateLoading, isStoreBlocked, blockedStoreContact } = useSiteSubscriptionGate({
        getStatus: HttpSiteSubscriptionStatus.get,
        getStoreInfo: HttpTiendaInfo.get,
        contactData: window.APP_CONFIG?.contactData,
    });

    useEffect(() => {
        document.documentElement.style.backgroundColor = theme.colors.background.default;
        document.documentElement.style.color = theme.colors.text.primary;
        document.body.style.backgroundColor = theme.colors.background.default;
        document.body.style.color = theme.colors.text.primary;
        document.body.style.fontFamily = theme.typography.fontFamily.primary;
    }, [theme]);

    const handleReactError = (error: Error, info: React.ErrorInfo) => {
        HttpLogger.logReactError({
            app: "FrontTienda",
            environment: (window as any).APP_ENV ?? "prod",
            release: (window as any).APP_VERSION ?? undefined,
            url: window.location.href,
            path: window.location.pathname + window.location.search,
            userAgent: navigator.userAgent,
            timestampUtc: new Date().toISOString(),
            errorName: error.name,
            errorMessage: error.message,
            errorStack: error.stack ?? null,
            componentStack: info?.componentStack ?? null,
            extra: {},
        });
    };

    return (
        <ThemeWrapper>
            <ErrorBoundary FallbackComponent={AppErrorFallback} onError={handleReactError}>
                {isStoreGateLoading ? (
                    <Box
                        sx={{
                            minHeight: "100vh",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: theme.colors.background.default,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : isStoreBlocked ? (
                    <StoreBlockedPage
                        whatsappUrl={blockedStoreContact?.whatsappUrl ?? null}
                        backgroundColor={theme.colors.background.default}
                        textColor={theme.colors.text.primary}
                        secondaryTextColor={theme.colors.text.secondary}
                        fontFamilyPrimary={theme.typography.fontFamily.primary}
                    />
                ) : (
                    <TiendaInfoProvider>
                        <SecurityObjectProvider>
                            <ShippingProvider>
                                <UserDataProvider>
                                    <PricingSessionProvider>
                                        <ProductCategoryProvider>
                                            <Router>
                                                <ScrollToTop />
                                                <Routes>
                                                    <Route path="/" element={<DefaultLayout />}>
                                                        <Route index element={<Home />} />
                                                        <Route
                                                            path="productos"
                                                            element={<ProductosPage />}
                                                        />
                                                        <Route
                                                            path="productos/:sku"
                                                            element={<ProductDetailPage />}
                                                        />
                                                        <Route
                                                            path="categoria/:sku"
                                                            element={
                                                                <ProductListing mode="category" />
                                                            }
                                                        />
                                                        <Route
                                                            path="/search"
                                                            element={<SearchResultsPage />}
                                                        />
                                                        <Route
                                                            path="/faqs"
                                                            element={<FAQsPage />}
                                                        />
                                                        <Route
                                                            path="/politica-de-privacidad"
                                                            element={<PoliticaPrivacidadPage />}
                                                        />

                                                        <Route
                                                            path="/login"
                                                            element={
                                                                <SafetyRouteMiddleware
                                                                    objectCode={
                                                                        TiendaSecurityObjects.LoginRoute
                                                                    }
                                                                    redirectToIfNoPermission="/"
                                                                >
                                                                    <LogIn />
                                                                </SafetyRouteMiddleware>
                                                            }
                                                        />
                                                        <Route
                                                            path="/recupero-contrasenia"
                                                            element={
                                                                <SafetyRouteMiddleware
                                                                    objectCode={
                                                                        TiendaSecurityObjects.LoginRoute
                                                                    }
                                                                    redirectToIfNoPermission="/"
                                                                >
                                                                    <RecuperoContrasenia />
                                                                </SafetyRouteMiddleware>
                                                            }
                                                        />
                                                        <Route
                                                            path="/registro"
                                                            element={
                                                                <SafetyRouteMiddleware
                                                                    objectCode={
                                                                        TiendaSecurityObjects.RegisterRoute
                                                                    }
                                                                    redirectToIfNoPermission="/"
                                                                >
                                                                    <Register />
                                                                </SafetyRouteMiddleware>
                                                            }
                                                        />
                                                        <Route
                                                            path="/perfil"
                                                            element={
                                                                <SafetyRouteMiddleware
                                                                    objectCode={
                                                                        TiendaSecurityObjects.ProfileRoute
                                                                    }
                                                                    requireAuth
                                                                    redirectToIfNotAuth="/login"
                                                                    redirectToIfNoPermission="/"
                                                                >
                                                                    <ProfilePage />
                                                                </SafetyRouteMiddleware>
                                                            }
                                                        />
                                                    </Route>

                                                    <Route element={<CheckoutFormContext />}>
                                                        <Route
                                                            path="/checkout"
                                                            element={
                                                                <CheckoutLayout>
                                                                    <CheckoutCartReconciliationGate reason="CHECKOUT_ENTER">
                                                                        <CheckoutPage />
                                                                    </CheckoutCartReconciliationGate>
                                                                </CheckoutLayout>
                                                            }
                                                        />
                                                    </Route>

                                                    {/* Custom Voda Argentina: actualizacion de datos personales, sin carrito/orden. */}
                                                    <Route
                                                        path="/actualizacion-datos"
                                                        element={
                                                            <CheckoutLayout>
                                                                <ActualizacionDatosPage />
                                                            </CheckoutLayout>
                                                        }
                                                    />

                                                    <Route
                                                        path="/checkout-reanudar/:token"
                                                        element={
                                                            <SafetyRouteMiddleware
                                                                objectCode={
                                                                    TiendaSecurityObjects.CheckoutResumeRoute
                                                                }
                                                                redirectToIfNoPermission="/"
                                                            >
                                                                <CheckoutResumeRoute />
                                                            </SafetyRouteMiddleware>
                                                        }
                                                    >
                                                        <Route element={<CheckoutFormContext />}>
                                                            <Route
                                                                index
                                                                element={
                                                                    <CheckoutLayout>
                                                                        <CheckoutCartReconciliationGate reason="CHECKOUT_RESUME">
                                                                            <CheckoutResumePage />
                                                                        </CheckoutCartReconciliationGate>
                                                                    </CheckoutLayout>
                                                                }
                                                            />
                                                        </Route>
                                                    </Route>

                                                    <Route
                                                        path="/checkout-reanudar-guid/:guid"
                                                        element={
                                                            <SafetyRouteMiddleware
                                                                objectCode={
                                                                    TiendaSecurityObjects.CheckoutResumeRoute
                                                                }
                                                                redirectToIfNoPermission="/"
                                                            >
                                                                <CheckoutResumeGuidRoute />
                                                            </SafetyRouteMiddleware>
                                                        }
                                                    >
                                                        <Route element={<CheckoutFormContext />}>
                                                            <Route
                                                                index
                                                                element={
                                                                    <CheckoutLayout>
                                                                        <CheckoutCartReconciliationGate reason="CHECKOUT_RESUME">
                                                                            <CheckoutResumePage />
                                                                        </CheckoutCartReconciliationGate>
                                                                    </CheckoutLayout>
                                                                }
                                                            />
                                                        </Route>
                                                    </Route>

                                                    <Route
                                                        path="/pago/mercadopago/success"
                                                        element={
                                                            <CheckoutLayout>
                                                                <MercadoPagoReturnPage />
                                                            </CheckoutLayout>
                                                        }
                                                    />
                                                    <Route
                                                        path="/pago/mercadopago/failure"
                                                        element={
                                                            <CheckoutLayout>
                                                                <MercadoPagoFailureReturnPage />
                                                            </CheckoutLayout>
                                                        }
                                                    />
                                                    <Route
                                                        path="/pago/mercadopago/pending"
                                                        element={
                                                            <CheckoutLayout>
                                                                <MercadoPagoReturnPage />
                                                            </CheckoutLayout>
                                                        }
                                                    />
                                                </Routes>
                                            </Router>
                                        </ProductCategoryProvider>
                                    </PricingSessionProvider>
                                </UserDataProvider>
                            </ShippingProvider>
                        </SecurityObjectProvider>
                    </TiendaInfoProvider>
                )}
            </ErrorBoundary>
        </ThemeWrapper>
    );
}

export default App;
